import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import { FC, useEffect, useState } from 'react';
import {
	Box,
	DialogContent,
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	TextField,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { getGpsrPlans, getGpsrProductTypes } from 'redux/reducers/gpsr/selectors';
import { editGpsrCustomerRequest, getGpsrPlansRequest } from 'redux/reducers/gpsr/reducer';
import { DatePickerComponent } from 'ui/DatePickerComponent';
import { TGpsrEditCustomerDialogProps } from './types';
import { validationSchema } from './validationSchema';

export const GpsrEditCustomerDialog: FC<TGpsrEditCustomerDialogProps> = ({ open, setClose }) => {
	const dispatch = useDispatch();
	const [numberOfProductTypes, setNumberOfProductTypes] = useState<string>('0');
	const [subscriptionPlan, setSubscriptionPlan] = useState<string>('');
	const [periodStart, setPeriodStart] = useState<string>('');
	const [periodEnd, setPeriodEnd] = useState<string>('');
	// const [dateStart, setDateStart] = useState<DateTime | null>(null);
	// const [dateEnd, setDateEnd] = useState<DateTime | null>(null);

	const gpsrPlans = useSelector(getGpsrPlans);
	const items = useSelector(getGpsrProductTypes);

	const reset = () => {
		setNumberOfProductTypes('');
		setSubscriptionPlan('');
		setPeriodStart('');
		setPeriodEnd('');
		// 	setDateStart(null);
		// 	setDateEnd(null);
	};

	const formikGpsr = useFormik({
		initialValues: {
			numberOfProductTypes,
			subscriptionPlan,
			periodStart,
			periodEnd,
			// dateStart,
			// dateEnd,
		},
		enableReinitialize: true,
		validationSchema,
		// onSubmit: (values) => {
		// 	console.log('onSubmit', values);
		// },

		onSubmit: values => {
			if (!items?.merchant) return;
			dispatch(
				editGpsrCustomerRequest({
					id: items?.merchant?.id ?? 0,
					numberOfAdditionalProductTypes: Number(values.numberOfProductTypes),
					subscriptionPlanId:
						gpsrPlans?.find(plan => plan.userVisibleName === values.subscriptionPlan)?.id ?? 0,
					periodStart: values.periodStart,
					periodEnd: values.periodEnd,
				}),
			);
			formikGpsr.resetForm();
			reset();
			setClose();
			setTimeout(() => {
				window.location.reload();
			}, 2000);
		},
	});

	useEffect(() => {
		dispatch(getGpsrPlansRequest());
	}, [dispatch]);

	useEffect(() => {
		if (!open) return;
		if (!items?.merchant) return;
		setNumberOfProductTypes(String(items?.merchant?.numberOfAdditionalProductTypes ?? '0'));
		setSubscriptionPlan(
			gpsrPlans?.find(plan => plan.id === (items?.merchant?.subscriptionPlanId ?? 0))
				?.userVisibleName ?? '',
		);
		setPeriodStart(items?.merchant?.periodStart ?? '');
		setPeriodEnd(items?.merchant?.periodEnd ?? '');
		// setDateStart(
		// 	items?.merchant?.periodStart ? DateTime.fromISO(items?.merchant?.periodStart) : null,
		// );
		// setDateEnd(items?.merchant?.periodEnd ? DateTime.fromISO(items?.merchant?.periodEnd) : null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [items?.merchant, open]);

	const handleDateStart = (date: DateTime | null) => {
		formikGpsr.setFieldValue('periodStart', date?.toISODate() ?? '').catch(error => {
			console.error('Error setting period Start:', error);
		});
	};

	const handleDateEnd = (date: DateTime | null) => {
		formikGpsr.setFieldValue('periodEnd', date?.toISODate() ?? '').catch(error => {
			console.error('Error setting period End:', error);
		});
	};

	return (
		<Dialog
			open={open}
			sx={{
				'& .MuiDialog-paper': {
					width: '600px',
					maxWidth: '600px',
					borderRadius: '12px',
				},
			}}
		>
			<DialogTitle color="primary">Edit Customer</DialogTitle>
			<form onSubmit={formikGpsr.handleSubmit}>
				<DialogContent
					sx={{
						p: '0 12px 0px 12px',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							gap: '16px',
						}}
					>
						<Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
							<TextField
								id="numberOfProductTypes"
								name="numberOfProductTypes"
								size="small"
								sx={{
									mt: '12px',
								}}
								label="Additional Product Types"
								value={formikGpsr.values.numberOfProductTypes}
								onChange={formikGpsr.handleChange}
								onBlur={formikGpsr.handleBlur}
								type="text"
								fullWidth
								error={
									formikGpsr.touched.numberOfProductTypes &&
									Boolean(formikGpsr.errors.numberOfProductTypes)
								}
								helperText={
									formikGpsr.touched.numberOfProductTypes && formikGpsr.errors.numberOfProductTypes
								}
							/>

							<FormControl fullWidth sx={{ mt: '12px' }}>
								<InputLabel id="subscriptionPlanLabel">Subscription Plan</InputLabel>
								<Select
									id="subscriptionPlan"
									labelId="subscriptionPlanLabel"
									label="Subscription Plan"
									name="subscriptionPlan"
									value={formikGpsr.values.subscriptionPlan}
									onChange={formikGpsr.handleChange}
									fullWidth
									size="small"
								>
									{gpsrPlans?.map?.(plan => (
										<MenuItem key={plan.id} value={plan.userVisibleName}>
											{plan.userVisibleName}
										</MenuItem>
									))}
								</Select>
								<FormHelperText>
									{formikGpsr.touched.subscriptionPlan && formikGpsr.errors.subscriptionPlan}
								</FormHelperText>
							</FormControl>

							<DatePickerComponent
								id="dateStart"
								value={DateTime.fromISO(formikGpsr.values.periodStart ?? '')}
								onChange={handleDateStart}
								label="Period Start"
							/>

							<DatePickerComponent
								id="dateEnd"
								value={DateTime.fromISO(formikGpsr.values.periodEnd ?? '')}
								onChange={handleDateEnd}
								label="Period End"
							/>

							{/* <LocalizationProvider dateAdapter={AdapterLuxon}>
								<DemoContainer components={['DatePicker']}>
									<DatePicker
										format="yyyy-MM-dd"
										value={dateStart}
										onChange={(newValue) => {
											setDateStart(newValue);
										}}
										label="Period Start"
										sx={{
											mt: '6px',
											width: '100%',
											minWidth: '100%',
										}}
										slotProps={{ textField: { size: 'small' } }}
									/>
								</DemoContainer>
							</LocalizationProvider>

							<LocalizationProvider dateAdapter={AdapterLuxon}>
								<DemoContainer
									components={['DatePicker']}
									sx={{
										mt: '6px',
									}}
								>
									<DatePicker
										format="yyyy-MM-dd"
										value={dateEnd}
										onChange={(newValue) => {
											setDateEnd(newValue);
										}}
										label="Period End"
										sx={{
											width: '100%',
											minWidth: '100%',
										}}
										disabled
										slotProps={{ textField: { size: 'small' } }}
									/>
								</DemoContainer>
							</LocalizationProvider> */}
						</Box>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							formikGpsr.resetForm();
							reset();
							setClose();
						}}
					>
						Cancel
					</Button>
					{/* <Button
						type="button"
						onClick={handleParse}
						autoFocus
					>
						Parse
					</Button> */}

					<Button
						type="submit"
						// onClick={() => {
						// 	console.log('Button send clicked');
						// 	formikGpsr.handleSubmit();
						// }}
						autoFocus
					>
						Send
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};
